import React, { Component, useEffect, useState, useContext } from 'react';
import {
    Row,
    Col,
    Container,
    Card,
    CardHeader,
    CardBody,
    TabContent,
    TabPane,
    Nav,
    NavItem,
    NavLink,
    Button,
    Label,
    Table,
    Input,
    ButtonGroup
} from "reactstrap";
import { api, ui_helpers, date_helpers } from "../helpers";
import {
    Header,
    Footer,
    ResultHeader,
    AdminHeader,
    Loading
} from "../components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Alert from "react-s-alert-v3";
import classnames from 'classnames';
import ToggleButton from "react-toggle-button";
import Select from "react-select";
import {Link, Redirect} from 'react-router-dom';
import _ from "lodash";
import { constants } from "../utils";
import useUserStore from '../hooks/useUserStore';

export default function Regions(props) {
  const PATHNAMES = constants.PATH_NAMES;
  const [states, setStates] = useState([]);
  const [contractTerms, setContractTerms] = useState([]);
  const [regionContractTerms, setRegionContractTerms] = useState([]);
  const [expandedRegionId, setExpandedRegionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [redirect, setRedirect] = useState('');
  const currentUser = useUserStore(x => x.currentUser);

    useEffect(() => {
      let isActive = true;
      getAllStates(isActive);
      getContractTerms(isActive);
      getRegionContractTerms(isActive);
      return () => {
        isActive = false;
      };
    }, []);

    function getAllStates(isActive) {
      api.fetch("reference/FullRegionList").then(response => {
        if (isActive) {
          setStates(ui_helpers.idNameToValueLabel(response.data.data));
        }
      }).catch(error => Alert.error("There was an error loading the states list."));
    }

    function getContractTerms(isActive) {
      api.fetch("reference/ContractTermList").then(response => {
        if (isActive) {
          setContractTerms(response.data.data);
        }
      }).catch(error => Alert.error("There was an error loading the contract term list."));
    }

    function getRegionContractTerms(isActive) {
      api.fetch("reference/AllRegionContractTerms").then(response => {
        if (isActive) {
          setRegionContractTerms(response.data.data);
        }
      }).catch(error => Alert.error("There was an error loading the region contract terms."));
    }

    function saveRegion(r) {
      api.post("reference/SaveRegion", r).then(response => {
        if (response.data.success) {
          setStates(_.map(states, s => {
            return s.id === r.id ? r : s;
          }));
        } else {
          Alert.error("There was an error saving the region: " + response.data.message);
        }
      }).catch(error => Alert.error("There was an error saving the region."));
    }

    // an active RegionContractTerm row means the term may be offered in that region;
    // combinations that have never been configured are shown as inactive but toggleable
    function resolveRegionTerms(regionId) {
      return _.map(contractTerms, t => {
        const existing = _.find(regionContractTerms,
          x => x.regionId === regionId && x.contractTermId === t.id && x.isActive);
        return existing
          ? existing
          : {id: 0, regionId: regionId, contractTermId: t.id, contractTermName: t.name, isActive: false};
      });
    }

    function summarizeAllowedTerms(regionId) {
      const allowed = _.filter(regionContractTerms, x => x.regionId === regionId && x.isActive);
      if (!allowed.length)
        return (<span/>); // (<span className="text-danger">None</span>);
      return _.chain(allowed)
        .sortBy(x => x.contractTermMultiplier)
        .map(x => x.contractTermName.replace(' Month', ''))
        .join(", ")
        .value();
    }

    function saveRegionContractTerm(regionTerm, newIsActive) {
      const payload = {...regionTerm, isActive: newIsActive};
      api.post("reference/SaveRegionContractTerm", payload).then(response => {
        if (response.data.success) {
          const saved = response.data.message;
          setRegionContractTerms(list => {
            const remainder = _.reject(list,
              x => x.regionId === payload.regionId && x.contractTermId === payload.contractTermId);
            return saved ? remainder.concat([saved]) : remainder;
          });
        } else {
          Alert.error("There was an error saving the region contract term: " + response.data.message);
        }
      }).catch(error => Alert.error("There was an error saving the region contract term."));
    }

    useEffect(() => {
      if (ui_helpers.isSystemAdministrator(currentUser)) return;
      setRedirect('/');
    }, []);

    if (redirect) {
      return <Redirect to={redirect}/>
    }

  return (<>
    <AdminHeader regions />
    <Container fluid>
      <CardHeader>
        <Row className='d-flex align-items-center'>
          <Col>
            <ResultHeader heading='States' totalResults={states ? states.length : 0}/>
          </Col>
        </Row>
      </CardHeader>
      <Table size="sm" bordered striped>
        <thead>
          <tr>
            <th>Name</th>
            <th>Allow Purchase Reserve?</th>
            <th>Allow Guess of Tax Rate Based on City and State?</th>
            <th>Allow Security Deposit?</th>
            <th>Allowed Terms</th>
            <th/>
          </tr>
        </thead>
        <tbody>
        {isLoading
          ? (<tr><td><Loading/></td></tr>)
          : (<>
              {states
                ? (_.map(states, s => (<React.Fragment key={`st-${s.id}`}>
                  <tr>
                  <td>{s.name}</td>
                  <td>
                    <ToggleButton
                      value={s.allowPurchaseReserve}
                      activeLabel="Yes"
                      inactiveLabel="No"
                      onToggle={(value) => saveRegion({...s, allowPurchaseReserve: !value})}
                    />
                  </td>
                  <td>
                    <ToggleButton
                      value={s.allowTaxRateDefaultGuess}
                      activeLabel="Yes"
                      inactiveLabel="No"
                      onToggle={(value) => saveRegion({...s, allowTaxRateDefaultGuess: !value})}
                    />
                  </td>
                  <td>
                    <ToggleButton
                      value={s.allowSecurityDeposit}
                      activeLabel="Yes"
                      inactiveLabel="No"
                      onToggle={(value) => saveRegion({...s, allowSecurityDeposit: !value})}
                    />
                  </td>
                  <td>{summarizeAllowedTerms(s.id)}</td>
                  <td>
                    <FontAwesomeIcon
                      title={`Click to ${expandedRegionId === s.id ? "Hide" : "Expand"} Contract Terms`}
                      onClick={() => setExpandedRegionId(expandedRegionId === s.id ? null : s.id)}
                      icon={expandedRegionId === s.id ? "chevron-circle-up" : "chevron-circle-down"}
                      className="float-end text-dark cursor ml-2"
                    />
                  </td>
                  </tr>
                  {expandedRegionId === s.id
                    ? (<tr>
                        <td colSpan={6} className="ps-3">
                          <Card>
                            <CardBody>
                              <Label className="fw-bold">Contract Terms Allowed in {s.name}</Label>
                              <Row>
                              {_.map(resolveRegionTerms(s.id), rt => (
                                <Col key={`rct-${s.id}-${rt.contractTermId}`} xs={6} sm={4} md={1} lg={1}
                                  className="d-flex align-items-center py-1">
                                  <ToggleButton
                                    value={rt.isActive}
                                    activeLabel="Yes"
                                    inactiveLabel="No"
                                    onToggle={(value) => saveRegionContractTerm(rt, !value)}
                                  />
                                  <span className="ps-2">{rt.contractTermName}</span>
                                </Col>
                              ))}
                              </Row>
                            </CardBody>
                          </Card>
                        </td>
                      </tr>)
                    : null
                  }
                </React.Fragment>)))
                : null
              }
            </>)
        }
        </tbody>
      </Table>
    </Container>
  </>);
}
